import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

const projectId = process.env.REACT_APP_SANITY_PROJECT_ID;
const dataset = process.env.REACT_APP_SANITY_DATASET || 'production';

export const sanityEnabled = Boolean(projectId);

export const sanityClient = sanityEnabled
  ? createClient({
      projectId,
      dataset,
      apiVersion: '2025-02-06',
      useCdn: true,
    })
  : null;

const builder = sanityClient ? imageUrlBuilder(sanityClient) : null;

export function urlFor(source) {
  if (!builder || !source) return null;
  try {
    return builder.image(source);
  } catch {
    return null;
  }
}

// Fallback example articles shown when Sanity isn't configured yet.
// Edit content in Sanity Studio to replace these.
export const fallbackArticles = [
  {
    _id: 'fb-1',
    title: 'Engineering AI Systems That Can Be Trusted In Production',
    slug: { current: 'engineering-trustworthy-ai-systems' },
    excerpt:
      'A practical framework for shipping AI features without compromising on reliability, privacy, or auditability — drawn from our work with Australian enterprises.',
    publishedAt: '2025-11-18T09:00:00.000Z',
    author: { name: 'Veventex Engineering' },
    category: 'AI Integration',
    coverImageUrl:
      'https://images.unsplash.com/photo-1749006590475-4592a5dbf99f?w=1600&q=80',
    body: [
      { _type: 'block', _key: '1', style: 'normal', children: [{ _type: 'span', _key: 's1', text: 'AI in production is no longer a research problem — it is a software engineering problem. Most of the failure modes we see are not exotic alignment edge cases; they are unmonitored prompts, untested retrieval, and unbounded inference costs.' }] },
      { _type: 'block', _key: '2', style: 'h2', children: [{ _type: 'span', _key: 's2', text: 'The four pillars' }] },
      { _type: 'block', _key: '3', style: 'normal', children: [{ _type: 'span', _key: 's3', text: 'Observability, evaluation, guardrails, and cost control. Treat each one as a first-class concern — not an afterthought wedged in after launch.' }] },
    ],
  },
  {
    _id: 'fb-2',
    title: 'Cross-Platform Without Compromise: Our 2026 Stack',
    slug: { current: 'cross-platform-without-compromise' },
    excerpt:
      'Why we still bet on a unified codebase for iOS, Android, and web — and the architectural decisions that make it feel native on every surface.',
    publishedAt: '2025-10-04T09:00:00.000Z',
    author: { name: 'Veventex Engineering' },
    category: 'Cross-Platform',
    coverImageUrl:
      'https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?w=1600&q=80',
    body: [
      { _type: 'block', _key: '1', style: 'normal', children: [{ _type: 'span', _key: 's1', text: 'A modern cross-platform stack should feel invisible to your users and inevitable to your engineers. We share the patterns that have made our delivery 40% faster without sacrificing platform fidelity.' }] },
    ],
  },
  {
    _id: 'fb-3',
    title: 'Red-Teaming Your Own LLM Application',
    slug: { current: 'red-teaming-your-llm-application' },
    excerpt:
      'A hands-on checklist for AI security: prompt injection surfaces, data exfiltration paths, model-in-the-middle attacks, and what to do about each.',
    publishedAt: '2025-08-22T09:00:00.000Z',
    author: { name: 'Veventex Security Lab' },
    category: 'AI Security',
    coverImageUrl:
      'https://images.pexels.com/photos/5380618/pexels-photo-5380618.jpeg?w=1600',
    body: [
      { _type: 'block', _key: '1', style: 'normal', children: [{ _type: 'span', _key: 's1', text: 'If you have shipped an LLM product, someone is already probing it. The good news is that most attacks fall into a small number of categories — and almost all of them can be neutralised with disciplined input handling and least-privilege tool access.' }] },
    ],
  },
];

const LIST_QUERY = `*[_type == "post"] | order(publishedAt desc) {
  _id, title, slug, excerpt, publishedAt, category,
  "author": author->{name},
  "coverImage": mainImage
}`;

const DETAIL_QUERY = `*[_type == "post" && slug.current == $slug][0] {
  _id, title, slug, excerpt, publishedAt, category,
  "author": author->{name},
  "coverImage": mainImage,
  body[]{ ..., _type == "image" => { ..., asset->{_id, url} } }
}`;

export async function fetchArticles() {
  if (!sanityClient) return fallbackArticles;
  try {
    const data = await sanityClient.fetch(LIST_QUERY);
    if (!data || data.length === 0) return fallbackArticles;
    return data.map((a) => ({
      ...a,
      coverImageUrl: a.coverImage ? urlFor(a.coverImage)?.width(1600).url() : null,
    }));
  } catch (e) {
    console.warn('Sanity fetch failed, using fallback', e);
    return fallbackArticles;
  }
}

export async function fetchArticleBySlug(slug) {
  if (!sanityClient) return fallbackArticles.find((a) => a.slug.current === slug) || null;
  try {
    const data = await sanityClient.fetch(DETAIL_QUERY, { slug });
    if (!data) return fallbackArticles.find((a) => a.slug.current === slug) || null;
    return {
      ...data,
      coverImageUrl: data.coverImage ? urlFor(data.coverImage)?.width(1600).url() : null,
    };
  } catch (e) {
    console.warn('Sanity fetch failed, using fallback', e);
    return fallbackArticles.find((a) => a.slug.current === slug) || null;
  }
}
